/**
 * Thẻ "Bảng tổng tháng": số từng tiểu đội và từng người do máy chủ tính
 * (`/sales-team/summary`). Máy chủ đã cắt theo phạm vi người xem (13a) — lương
 * cứng người khác trả 0 khi người xem không được thấy.
 *
 * Việc chặn chốt (CEO 1a, 30/10/2026): một dải đỏ gọn ở đầu thẻ đếm theo nhóm;
 * bấm "Xem từng việc" mới mở danh sách gom theo tiểu đội, mỗi nhóm có nút sang
 * đúng thẻ để xử lý. Số tiền đứng ngay dưới dải, không bị đẩy xuống cuối trang.
 * Lời từng việc giữ đúng lời máy chủ.
 */
import { useState } from "react";
import { Button } from "@/ui/button";
import { Badge } from "@/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { QueryErrorNote } from "@/components/QueryErrorNote";
import { formatVND } from "@/lib/format";
import { type SalesOrgView, type SalesTeamSummary, useSalesTeamSummary } from "../api";
import { PERSON_COLS, bySquad, criterionOf, groupWarnings, summaryCsv, variableOf, type WarnGroup } from "../helpers";
import { monthText, percentFromBp } from "../labels";
import type { SalesTeamTab } from "../SalesTeamView";
import { quarterLabel } from "@/features/payroll/salesTeamSlip";
import { QuarterReview } from "./QuarterReview";
import { PersonMark, SquadMark } from "./marks";

function downloadCsv(s: SalesTeamSummary) {
  const blob = new Blob(["﻿" + summaryCsv(s)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `phong-kinh-doanh-${s.month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Số dòng lời máy chủ hiện sẵn ở mỗi nhóm; còn lại bấm "Hiện thêm". */
const SHOW_FIRST = 3;

type Warning = SalesTeamSummary["warnings"][number];

function MessageList({ items }: { items: Warning[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, SHOW_FIRST);
  return (
    <>
      <ul className="list-disc space-y-0.5 pl-5">
        {shown.map((w, i) => (
          <li key={`${w.code}-${i}`}>{w.message}</li>
        ))}
      </ul>
      {items.length > SHOW_FIRST ? (
        <button type="button" className="min-h-11 text-sm font-medium underline" onClick={() => setAll(!all)}>
          {all ? "Thu gọn" : `Hiện thêm ${items.length - SHOW_FIRST} việc`}
        </button>
      ) : null}
    </>
  );
}

type GroupProps = { group: WarnGroup; squadName: (id: string) => string; onGoTab?: ((t: SalesTeamTab) => void) | undefined };

function BlockingGroup({ group, squadName, onGoTab }: GroupProps) {
  const { squads, rest } = bySquad(group.items);
  return (
    <section aria-label={`${group.label}: ${group.items.length} việc`} className="space-y-2 border-t border-border pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-foreground">
          {group.label} <span className="tabular-nums text-destructive">· {group.items.length}</span>
        </p>
        {group.tab && onGoTab ? (
          <Button variant="outline" onClick={() => onGoTab(group.tab)}>
            Đi xử lý →
          </Button>
        ) : null}
      </div>
      {group.id === "target" && rest.length === 0 ? (
        <>
          <p className="text-muted">Chưa có mục tiêu tháng — chưa tính được thưởng vượt:</p>
          <ul className="flex flex-wrap gap-2">
            {squads.map((q) => (
              <li key={q.squadId} className="rounded-full border border-border py-1 pl-1 pr-3">
                <SquadMark squadId={q.squadId} name={squadName(q.squadId)} size={24} />
              </li>
            ))}
          </ul>
        </>
      ) : group.id === "quality" && rest.length === 0 ? (
        <ul className="grid grid-cols-1 gap-x-4 gap-y-1.5 md:grid-cols-2">
          {squads.map((q) => (
            <li key={q.squadId} className="flex min-w-0 flex-col gap-0.5">
              <SquadMark squadId={q.squadId} name={squadName(q.squadId)} size={24} className="font-medium" />
              <span className="text-muted">
                chưa có kết quả {q.items.map((w) => `«${criterionOf(w.message)}»`).join(", ")}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <MessageList items={group.items} />
      )}
    </section>
  );
}

type BlockingProps = {
  month: string;
  blocking: Warning[];
  squadName: (id: string) => string;
  onGoTab?: ((t: SalesTeamTab) => void) | undefined;
};

function BlockingStrip({ month, blocking, squadName, onGoTab }: BlockingProps) {
  const [open, setOpen] = useState(false);
  const groups = groupWarnings(blocking);
  return (
    <div role="alert" className="space-y-3 rounded-lg border border-destructive/50 bg-surface p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-destructive">
          Chưa chốt được tháng {monthText(month)} — còn {blocking.length} việc phải xử lý
        </p>
        <Button variant="outline" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? "Thu gọn" : "Xem từng việc"}
        </Button>
      </div>
      <ul className="flex flex-wrap gap-2" aria-label="Số việc theo nhóm">
        {groups.map((g) => (
          <li key={g.id} className="rounded-full bg-destructive/10 px-3 py-1 text-destructive">
            {g.label} <span className="font-semibold tabular-nums">{g.items.length}</span>
          </li>
        ))}
      </ul>
      {open
        ? groups.map((g) => <BlockingGroup key={g.id} group={g} squadName={squadName} onGoTab={onGoTab} />)
        : null}
    </div>
  );
}

function NotesLine({ notes }: { notes: Warning[] }) {
  const [open, setOpen] = useState(false);
  const allSplit = notes.every((w) => w.code === "credit_missing_split");
  return (
    <div className="space-y-1 text-sm text-muted">
      <p className="flex flex-wrap items-center gap-x-2">
        <span>
          {allSplit
            ? `${notes.length} đơn chưa ghi công — máy đang chia đều cho các bạn sales của tiểu đội`
            : `${notes.length} lưu ý không chặn chốt`}
        </span>
        <button type="button" className="min-h-11 font-medium underline" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? "Ẩn" : "Xem"}
        </button>
      </p>
      {open ? (
        <ul className="list-disc space-y-0.5 pl-5">
          {notes.map((w, i) => (
            <li key={`${w.code}-${i}`}>{w.message}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

type Props = {
  month: string;
  /** Tên tiểu đội theo mã cho dải việc chặn chốt (lời máy chủ chỉ có mã). */
  org?: SalesOrgView | undefined;
  /** Nút "Đi xử lý" sang đúng thẻ. */
  onGoTab?: ((t: SalesTeamTab) => void) | undefined;
};

export function SummaryTab({ month, org, onGoTab }: Props) {
  const summary = useSalesTeamSummary(month);
  if (summary.isError) return <QueryErrorNote error={summary.error} fallback="Không tải được bảng tổng tháng" />;
  if (summary.isLoading || !summary.data) return <p className="text-sm text-muted">Đang tải…</p>;
  const s = summary.data;
  const blocking = s.warnings.filter((w) => w.blocking);
  const notes = s.warnings.filter((w) => !w.blocking);
  const squadName = (id: string) =>
    s.squads.find((q) => q.squadId === id)?.name ?? org?.units.find((u) => u.id === id)?.name ?? id;

  return (
    <div className="space-y-6">
      {blocking.length > 0 || notes.length > 0 ? (
        <div className="space-y-2">
          {blocking.length > 0 ? (
            <BlockingStrip month={month} blocking={blocking} squadName={squadName} onGoTab={onGoTab} />
          ) : null}
          {notes.length > 0 ? <NotesLine notes={notes} /> : null}
        </div>
      ) : null}

      <section aria-labelledby="tong-tieu-doi" className="space-y-2">
        <h3 id="tong-tieu-doi" className="text-base font-semibold">
          Từng tiểu đội
        </h3>
        {s.squads.length === 0 ? (
          <EmptyState title="Chưa có số của tiểu đội nào trong tháng này" />
        ) : (
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {s.squads.map((q) => (
              <li key={q.squadId} className="space-y-1 rounded-md border border-border bg-surface p-3 text-sm">
                <p className="font-semibold">
                  <SquadMark squadId={q.squadId} name={q.name} size={28} />
                </p>
                <p>
                  Doanh thu thật: <span className="tabular-nums">{formatVND(q.realRevenue)}</span>
                </p>
                <p>
                  Doanh thu xét mục tiêu: <span className="tabular-nums">{formatVND(q.targetRevenue)}</span>
                </p>
                {/* div, không p: nhãn "Chưa đặt" là khối div, p chứa div là sai cấu trúc trang. */}
                <div>
                  Mục tiêu:{" "}
                  {q.hasTarget ? (
                    <>
                      <span className="tabular-nums">{formatVND(q.target)}</span>
                      {q.target > 0 ? (
                        <span className="text-muted">
                          {" "}
                          · đạt {Math.round((q.targetRevenue / q.target) * 100)}%
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <Badge variant="yellow">Chưa đặt</Badge>
                  )}
                </div>
                <p>Mức chất lượng: {percentFromBp(q.qualityBp)}</p>
                <p>
                  Túi tiểu đội: <span className="tabular-nums">{formatVND(q.pocketOpened)}</span>
                  {q.pocketKept > 0 ? (
                    <span className="text-muted">
                      {" "}
                      · công ty giữ <span className="whitespace-nowrap">{formatVND(q.pocketKept)}</span>
                    </span>
                  ) : null}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {quarterLabel(month) ? <QuarterReview month={month} label={quarterLabel(month)} data={s.quarter} /> : null}

      <section aria-labelledby="tong-tung-nguoi" className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="tong-tung-nguoi" className="text-base font-semibold">
            Từng người
          </h3>
          {s.people.length > 0 ? (
            <Button variant="outline" onClick={() => downloadCsv(s)}>
              Tải số cho kế toán
            </Button>
          ) : null}
        </div>
        {s.people.length === 0 ? (
          <EmptyState title="Chưa có người nào trong bảng tháng này" />
        ) : (
          <ul className="space-y-3">
            {s.people.map((p) => (
              <li key={p.staffId} className="space-y-2 rounded-md border border-border bg-surface p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <PersonMark staffId={p.staffId} name={p.name || p.staffId} size={28} className="font-semibold" />
                  {p.partial ? (
                    <span className="text-xs text-muted">Chỉ hiện phần thuộc tiểu đội bạn được xem</span>
                  ) : (
                    <span className="tabular-nums font-semibold">{formatVND(variableOf(p))}</span>
                  )}
                </div>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-4">
                  {/* Máy chủ chỉ xoá lương cứng NGƯỜI KHÁC; dòng của chính mình giữ số. */}
                  {!s.baseSalaryHidden || p.staffId === s.myStaffId ? (
                    <div className="flex justify-between gap-2">
                      <dt className="text-muted">Lương cứng đủ tháng</dt>
                      <dd className="tabular-nums">{formatVND(p.baseFull)}</dd>
                    </div>
                  ) : null}
                  {PERSON_COLS.filter((c) => (p[c.key] as number) !== 0).map((c) => (
                    <div key={c.key} className="flex justify-between gap-2">
                      <dt className="text-muted">{c.label}</dt>
                      <dd className="tabular-nums">{formatVND(p[c.key] as number)}</dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted">
          Số trên đây là tạm tính tới lúc xem. Lương cứng chia theo ngày công ở phiếu lương.
        </p>
      </section>
    </div>
  );
}
